/**
 * Inlined from Horizon @theme/utilities (subset used by localization).
 */
const requestIdleCallback =
  typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

function normalizeString(str) {
  return str.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }
    return acc;
  }, /** @type {Promise<unknown>[]} */ ([]));
  return Promise.allSettled(animationPromises).then(callback);
}

function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
}

function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }
  return !element.contains(event.target);
}



/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

/**
 * Base class that powers our custom web components.
 *
 * Manages references to child elements with `ref` attributes and sets up mutation observers to keep
 * the refs updated when the DOM changes. Also handles declarative event listeners using.
 *
 * @template {Refs} [T=Refs]
 * @extends {DeclarativeShadowElement}
 */
class Component extends DeclarativeShadowElement {
  /**
   * An object holding references to child elements with `ref` attributes.
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

  /**
   * An array of required refs. If a ref is not found, an error will be thrown.
   *
   * @type {string[] | undefined}
   */
  requiredRefs;

  /**
   * Gets the root node of the component, which is either its shadow root or the component itself.
   *
   * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }

  /**
   * Called when the element is connected to the document's DOM.
   *
   * Initializes event listeners and refs.
   */
  connectedCallback() {
    super.connectedCallback();
    registerEventListeners();

    this.#updateRefs();

    requestIdleCallback(() => {
      for (const root of this.roots) {
        this.#mutationObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['ref'],
          attributeOldValue: true,
        });
      }
    });
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    this.#mutationObserver.takeRecords();
    this.#updateRefs();
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   *
   * Disconnects the mutation observer.
   */
  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  #updateRefs() {
    const refs = /** @type any */ ({});
    const elements = this.roots.reduce((acc, root) => {
      for (const element of root.querySelectorAll('[ref]')) {
        if (!this.#isDescendant(element)) continue;
        acc.add(element);
      }

      return acc;
    }, /** @type {Set<Element>} */ (new Set()));

    for (const ref of elements) {
      const refName = ref.getAttribute('ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];

        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }

    this.refs = /** @type {RefsType<T>} */ (refs);
  }

  /**
   * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
   *
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === 'attributes' && this.#isDescendant(m.target)) ||
          (m.type === 'childList' && [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant))
      )
    ) {
      this.#updateRefs();
    }
  });

  /**
   * Checks if a given node is a descendant of this component.
   *
   * @param {Node} node - The node to check.
   * @returns {boolean} True if the node is a descendant of this component.
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;
}

/**
 * Get the ancestor of a given node.
 *
 * @param {Node} node - The node to get the ancestor of.
 * @returns {Node | null} The ancestor of the node or null if none is found.
 */
function getAncestor(node) {
  if (node.parentNode) return node.parentNode;

  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;

  return null;
}

/**
 * Recursively finds the closest ancestor that is an instance of `Component`.
 *
 * @param {Node | null} node - The starting node to search from.
 * @returns {HTMLElement | null} The closest ancestor `Component` instance or null if none is found.
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof Component) return node;
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith('-component')) return node;

  const ancestor = getAncestor(node);
  if (ancestor) return getClosestComponent(ancestor);

  return null;
}

/**
 * Initializes the event listeners for custom event handling.
 *
 * Sets up event listeners for specified events and delegates the handling of those events
 * to methods defined on the closest `Component` instance, based on custom attributes.
 */
let initialized = false;

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  const events = ['click', 'change', 'select', 'focus', 'blur', 'submit', 'input', 'keydown', 'keyup', 'toggle'];
  const shouldBubble = ['focus', 'blur'];
  const expensiveEvents = ['pointerenter', 'pointerleave'];

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event);

        if (!element) return;

        const proxiedEvent =
          event.target !== element
            ? new Proxy(event, {
                get(target, property) {
                  if (property === 'target') return element;

                  const value = Reflect.get(target, property);

                  if (typeof value === 'function') {
                    return value.bind(target);
                  }

                  return value;
                },
              })
            : event;

        const value = element.getAttribute(attribute) ?? '';
        let [selector, method] = value.split('/');
        // Extract the last segment of the attribute value delimited by `?` or `/`
        // Do not use lookback for Safari 16.0 compatibility
        const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
        const data = matches ? matches[2] : null;
        const instance = selector
          ? selector.startsWith('#')
            ? document.querySelector(selector)
            : element.closest(selector)
          : getClosestComponent(element);

        if (!(instance instanceof Component) || !method) return;

        method = method.replace(/\?.*/, '');

        const callback = /** @type {any} */ (instance)[method];

        if (typeof callback === 'function') {
          try {
            /** @type {(Event | Data)[]} */
            const args = [proxiedEvent];

            if (data) args.unshift(parseData(data));

            callback.call(instance, ...args);
          } catch (error) {
            console.error(error);
          }
        }
      },
      { capture: true }
    );
  }

  /** @param {Event} event */
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target;

    if (!(target instanceof Element)) return;

    if (target.hasAttribute(`on:${event.type}`)) {
      return target;
    }

    if (expensiveEvents.includes(event.type)) {
      return null;
    }

    return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
  }
}

/**
 * Parses a string to extract data based on a delimiter.
 *
 * @param {string} str - The string to parse.
 * @returns {Object|Array<string|number>|string} The parsed data.
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
      )
    : parseValue(data);
}

/**
 * @typedef {Object|Array<string|number>|string} Data
 */

/**
 * Parses a string value to its appropriate type.
 *
 * @param {string} str - The string to parse.
 * @returns {Data} The parsed value.
 */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;

  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

  return str;
}

/**
 * Throws a formatted error when a required ref is not found in the component.
 */
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLUListElement[]} countryListItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLSelectElement} languageInput - The language input element.
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 * @property {HTMLLIElement[]} [coreCountryItems] - Hard-coded core country rows (large catalogs).
 * @property {HTMLLIElement[]} [virtualEuropePool] - Recycled rows for Europe market virtual list.
 * @property {HTMLLIElement[]} [virtualInternationalPool] - Recycled rows for International market virtual list.
 *
 * @extends {Component<FormRefs>}
 */
class LocalizationFormComponent extends Component {
  /** @type {ResizeObserver | null} */
  #virtualListResizeObserver = null;

  /** @type {number} */
  #sharedVirtualRowHeight = 48;

  /**
   * @type {{
   *   key: string;
   *   data: { iso: string; name: string; cur: string; al: string }[];
   *   filtered: { iso: string; name: string; cur: string; al: string }[] | null;
   *   track: HTMLElement;
   *   windowEl: HTMLElement;
   *   hostLi: HTMLElement | null;
   *   pool: HTMLLIElement[];
   * }[] | null}
   */
  #virtualSegments = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.search && this.refs.search.addEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('scroll', this.#onCountryListScroll);

    this.resizeLanguageInput();

    requestAnimationFrame(() => {
      this.#initCountryVirtualList();
      requestAnimationFrame(() => this.#syncVirtualSegments());
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#virtualListResizeObserver?.disconnect();
    this.#virtualListResizeObserver = null;
    this.#virtualSegments = null;
    requestAnimationFrame(() => {
      this.#initCountryVirtualList();
      requestAnimationFrame(() => this.#syncVirtualSegments());
    });
  }

  disconnectedCallback() {
    this.#virtualListResizeObserver?.disconnect();
    this.#virtualListResizeObserver = null;
    this.#virtualSegments = null;
    this.refs.search && this.refs.search.removeEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('scroll', this.#onCountryListScroll);
    super.disconnectedCallback();
  }

  #initCountryVirtualList() {
    if (this.dataset.countryVirtual !== 'true' || this.#virtualSegments) return;

    const scripts = [
      ...this.querySelectorAll('script[type="application/json"][data-country-virtual-json][data-virtual-segment]'),
    ];
    if (!scripts.length) return;

    const POOL_REFS = {
      europe: 'virtualEuropePool',
      international: 'virtualInternationalPool',
    };

    const segments = [];

    for (const script of scripts) {
      const key = script.getAttribute('data-virtual-segment');
      if (!key || !Object.prototype.hasOwnProperty.call(POOL_REFS, key)) continue;

      const track = this.querySelector(`[data-country-virtual-track][data-virtual-segment="${key}"]`);
      if (!(track instanceof HTMLElement)) continue;
      const windowEl = track.querySelector(`[data-country-virtual-window][data-virtual-segment="${key}"]`);
      if (!(windowEl instanceof HTMLElement)) continue;

      let data;
      try {
        const raw = script.textContent?.trim();
        data = raw ? JSON.parse(raw) : [];
      } catch {
        continue;
      }

      const poolRefName = POOL_REFS[/** @type {'europe' | 'international'} */ (key)];
      const pool = /** @type {unknown} */ (this.refs)[poolRefName];
      if (!Array.isArray(pool) || pool.length === 0) continue;

      const hostLi = track.closest('li');
      segments.push({
        key,
        data: Array.isArray(data) ? data : [],
        filtered: null,
        track,
        windowEl,
        hostLi,
        pool: /** @type {HTMLLIElement[]} */ (pool),
      });
    }

    if (!segments.length) return;

    const sample =
      this.querySelector('.popular-countries .localization-form__list-item') ||
      this.querySelector('.localization-form__list-item[role="option"][data-value]:not([data-virtual-pool])');
    this.#sharedVirtualRowHeight =
      sample instanceof HTMLElement && sample.offsetHeight > 0 ? Math.max(40, sample.offsetHeight) : 48;

    for (const seg of segments) {
      if (seg.data.length === 0) {
        seg.track.style.height = '0';
        if (seg.hostLi instanceof HTMLElement) seg.hostLi.hidden = true;
        const label = this.querySelector(`[data-country-region-label="${seg.key}"]`);
        if (label instanceof HTMLElement) label.hidden = true;
      }
    }

    this.#virtualSegments = segments;

    if (typeof ResizeObserver !== 'undefined' && this.refs.countryList) {
      this.#virtualListResizeObserver?.disconnect();
      this.#virtualListResizeObserver = new ResizeObserver(() => {
        const s =
          this.querySelector('.popular-countries .localization-form__list-item') ||
          this.querySelector('.localization-form__list-item[role="option"][data-value]:not([data-virtual-pool])');
        if (s instanceof HTMLElement && s.offsetHeight > 0) {
          this.#sharedVirtualRowHeight = Math.max(40, s.offsetHeight);
          this.#syncVirtualSegments();
        }
      });
      this.#virtualListResizeObserver.observe(this.refs.countryList);
    }
  }

  /**
   * @param {HTMLElement} scrollRoot
   * @param {HTMLElement} el
   */
  #offsetTopWithinScrollRoot(scrollRoot, el) {
    let y = 0;
    let n = el;
    while (n && n !== scrollRoot) {
      y += n.offsetTop;
      const next = n.offsetParent;
      n = next instanceof HTMLElement ? next : null;
    }
    if (n === scrollRoot) return y;
    y = 0;
    n = el;
    while (n && n !== scrollRoot) {
      y += n.offsetTop;
      n = n.parentElement;
    }
    return n === scrollRoot ? y : 0;
  }

  #getCoreCountryItems() {
    return Array.isArray(this.refs.coreCountryItems) ? this.refs.coreCountryItems : [];
  }

  #getStaticCountryItems() {
    if (this.dataset.countryVirtual === 'true') return [];
    return Array.isArray(this.refs.countryListItems) ? this.refs.countryListItems : [];
  }

  #getFocusableCountryItems() {
    const core = this.#getCoreCountryItems();
    const staticRows = this.#getStaticCountryItems();
    /** @type {HTMLLIElement[]} */
    const poolItems = [];
    if (this.#virtualSegments) {
      for (const seg of this.#virtualSegments) {
        for (const li of seg.pool) {
          if (!li.hidden) poolItems.push(li);
        }
      }
    }
    return [...core, ...staticRows, ...poolItems].filter((li) => !li.hasAttribute('hidden'));
  }

  #extraRowMatchesSearch(searchValue, row) {
    const el = document.createElement('div');
    el.dataset.value = row.iso;
    if (row.al) el.dataset.aliases = row.al;
    el.innerHTML = `<span class="country">${row.name}</span><span class="localization-form__currency">${row.cur}</span>`;
    const m = this.#findMatches(searchValue, el);
    return Boolean(m.label || m.alias || m.iso || m.currency);
  }

  #paintVirtualPoolRow(li, row, searchValue, selectedIso) {
    li.dataset.value = row.iso;
    if (row.al) {
      li.dataset.aliases = row.al;
    } else {
      li.removeAttribute('data-aliases');
    }

    const bid = this.dataset.blockId ?? 'localization';
    li.id = `country-opt-${bid}-${row.iso}`;
    li.setAttribute('aria-label', row.name);

    if (row.iso === selectedIso) {
      li.setAttribute('aria-current', 'true');
    } else {
      li.removeAttribute('aria-current');
    }

    const leadTpl = this.querySelector(
      row.iso === selectedIso
        ? '.popular-countries > li[aria-current="true"] > span:first-child'
        : '.popular-countries > li:not([aria-current="true"]) > span:first-child'
    );
    const lead = li.children[0];
    if (leadTpl && lead) {
      lead.replaceWith(leadTpl.cloneNode(true));
    }

    const countrySpan = li.querySelector('.country');
    const curSpan = li.querySelector('.localization-form__currency');
    if (countrySpan) {
      if (searchValue) {
        countrySpan.innerHTML = this.#highlightMatches(row.name, searchValue);
      } else {
        countrySpan.textContent = row.name;
      }
    }
    if (curSpan) {
      curSpan.textContent = row.cur;
    }
    li.hidden = false;
  }

  #syncVirtualSegments() {
    if (!this.#virtualSegments?.length || !this.refs.countryList) return;

    const countryList = this.refs.countryList;
    const scrollTop = countryList.scrollTop;
    const viewH = countryList.clientHeight;
    const rowH = this.#sharedVirtualRowHeight;

    for (const seg of this.#virtualSegments) {
      const active = seg.filtered != null ? seg.filtered : seg.data;

      if (active.length === 0) {
        seg.track.style.height = '0';
        for (const li of seg.pool) {
          li.hidden = true;
        }
        seg.windowEl.style.transform = 'translateY(0)';
        continue;
      }

      const trackTop = this.#offsetTopWithinScrollRoot(countryList, seg.track);
      const scrollPastTrack = scrollTop - trackTop;
      const start = Math.min(
        active.length - 1,
        Math.max(0, Math.floor(Math.max(0, scrollPastTrack) / rowH))
      );
      const overscan = 4;
      const visibleCount = Math.min(seg.pool.length, Math.ceil(viewH / rowH) + overscan);
      const end = Math.min(active.length, start + visibleCount);

      seg.track.style.height = `${active.length * rowH}px`;
      seg.windowEl.style.transform = `translateY(${start * rowH}px)`;

      const searchRaw =
        this.refs.search instanceof HTMLInputElement ? normalizeString(this.refs.search.value) : '';
      const selectedIso = this.refs.countryInput?.value ?? '';

      let poolIx = 0;
      for (let i = start; i < end; i++) {
        const row = active[i];
        const li = seg.pool[poolIx];
        if (!row || !li) break;
        this.#paintVirtualPoolRow(li, row, searchRaw, selectedIso);
        poolIx++;
      }
      for (let j = poolIx; j < seg.pool.length; j++) {
        seg.pool[j].hidden = true;
      }
    }
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryInput, form } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('DOWN');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        const focusableItems = this.#getFocusableCountryItems();
        const focusedItem = focusableItems.find((item) => item.getAttribute('aria-selected') === 'true');

        if (focusedItem && countryInput && form) {
          countryInput.value = focusedItem.dataset.value ?? '';
          form.submit();
        }
        break;
      }
    }

    if (!this.refs.search) return;

    setTimeout(() => {
      const focusableItems = this.#getFocusableCountryItems();
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute('aria-activedescendant', focusedItem.id);
      } else {
        this.refs.search.setAttribute('aria-activedescendant', '');
      }
    });
  };

  /**
   * Selects a country.
   *
   * @param {string} countryName - The name of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (countryName, event) => {
    event.preventDefault();
    const { countryInput, form } = this.refs;

    countryInput.value = countryName;
    form?.submit();
  };

  /**
   * @param {Event} event
   */
  selectCountryFromVirtual(event) {
    const li = event.target instanceof Element ? event.target.closest('li[data-virtual-pool]') : null;
    if (!(li instanceof HTMLLIElement) || !this.contains(li)) return;
    const code = li.dataset.value;
    if (code) this.selectCountry(code, event);
  }

  /**
   * Changes the language of the localization form.
   *
   * @param {Event} event - The event object.
   */
  changeLanguage(event) {
    const { form, languageInput } = this.refs;
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value) {
      languageInput.value = value;
      this.resizeLanguageInput();
      form.submit();
    }
  }

  resizeLanguageInput() {
    const { languageInput } = this.refs;

    if (!languageInput) return;

    // Mobile menu localization (nf-mobile-localization): fit-content width + option label juggling
    // breaks native <select> picker anchoring on iOS Safari. Keep full options + no inline width.
    if (languageInput.closest('.nf-mobile-localization')) {
      languageInput.style.removeProperty('width');
      return;
    }

    // Hide all options except the selected option
    for (const option of languageInput.options) {
      if (!option.selected) {
        option.dataset.optionLabel = option.textContent || '';
        option.innerText = '';
      }
    }

    languageInput.style.width = 'fit-content';
    const measured = Math.ceil(languageInput.offsetWidth);

    // Restore labels before returning — required when the form is still display:none (offsetWidth === 0).
    for (const option of languageInput.options) {
      if (option.dataset.optionLabel) {
        option.textContent = option.dataset.optionLabel;
        delete option.dataset.optionLabel;
      }
    }

    if (measured > 0) {
      languageInput.style.width = `${measured + 1}px`;
    } else {
      languageInput.style.removeProperty('width');
    }
  }

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(
    searchValue,
    countryEl,
    options = {
      // Which data types (label, alias, iso) to match against
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      // If true, the search value must match the start of the label
      labelMatchStart: false,
      // If true, a result will not display unless the search value equals an alias in its entirety
      aliasExactMatch: false,
    }
  ) {
    let matchTypes = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const countryName = normalizeString(countryEl.querySelector('.country')?.textContent ?? '');

      if (!countryName) return matchTypes;

      matchTypes.label = options.labelMatchStart
        ? countryName.startsWith(searchValue)
        : countryName.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(countryEl.querySelector('.localization-form__currency')?.textContent ?? '');
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? '') == searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(',')?.map((alias) => normalizeString(alias));

      if (!countryAliases) return matchTypes;

      matchTypes.alias =
        countryAliases.length > 0 &&
        countryAliases.find((alias) =>
          options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue)
        ) !== undefined;
    }

    return matchTypes;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) return text ?? '';

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) return text;

    const endIndex = startIndex + normalizedSearch.length;
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);

    let result = '';
    if (before) {
      result += `<mark>${before}</mark>`;
    }
    result += match;
    if (after) {
      result += `<mark>${after}</mark>`;
    }
    return result;
  }

  /**
   * Filters the countries based on the search value.
   */
  filterCountries() {
    const { countryList, liveRegion, noResultsMessage, popularCountries, resetButton, search } = this.refs;
    if (!search || !countryList) return;

    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    resetButton?.toggleAttribute('hidden', !searchValue);

    if (popularCountries) {
      popularCountries.toggleAttribute('hidden', Boolean(searchValue));
    }

    const wrapper = this.querySelector('.country-selector-form__wrapper');
    if (wrapper) {
      wrapper.classList.toggle('is-searching', !!searchValue);
    }

    const processRow = (/** @type {HTMLElement} */ countryEl) => {
      if (searchValue === '') {
        countryEl.removeAttribute('hidden');
        const countrySpan = countryEl.querySelector('.country');
        if (countrySpan) {
          countrySpan.textContent = countrySpan.textContent;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, countryEl);

        if (matches.label || matches.alias || matches.iso || matches.currency) {
          countryEl.removeAttribute('hidden');
          const countrySpan = countryEl.querySelector('.country');
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          countryEl.setAttribute('hidden', '');
        }
      }
    };

    for (const countryEl of this.#getCoreCountryItems()) {
      processRow(countryEl);
    }

    let virtualMatchCount = 0;
    if (this.#virtualSegments) {
      for (const seg of this.#virtualSegments) {
        const label = this.querySelector(`[data-country-region-label="${seg.key}"]`);
        if (searchValue === '') {
          seg.filtered = null;
          virtualMatchCount += seg.data.length;
          if (seg.hostLi) seg.hostLi.hidden = seg.data.length === 0;
          if (label instanceof HTMLElement) label.hidden = seg.data.length === 0;
        } else {
          seg.filtered = seg.data.filter((row) => this.#extraRowMatchesSearch(searchValue, row));
          const n = seg.filtered.length;
          virtualMatchCount += n;
          if (seg.hostLi) seg.hostLi.hidden = n === 0;
          if (label instanceof HTMLElement) label.hidden = n === 0;
        }
      }
      countVisibleCountries += virtualMatchCount;
    } else {
      for (const countryEl of this.#getStaticCountryItems()) {
        processRow(countryEl);
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace('[count]', `${countVisibleCountries}`);
    }

    if (noResultsMessage) {
      noResultsMessage.hidden = countVisibleCountries > 0;
    }
    countryList.scrollTop = 0;
    this.#syncVirtualSegments();
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const focusableItems = this.#getFocusableCountryItems();
    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
    const focusedItem = focusableItems[focusedItemIndex];
    let itemToFocus;

    if (direction === 'UP') {
      itemToFocus =
        focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
    }

    if (focusedItem) {
      focusedItem.setAttribute('aria-selected', 'false');
    }
    itemToFocus?.setAttribute('aria-selected', 'true');
    itemToFocus?.focus({ preventScroll: true });
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  resetCountriesFilter(event) {
    const { search } = this.refs;

    event.stopPropagation();
    search.value = '';
    this.filterCountries();
    search.setAttribute('aria-activedescendant', '');
    search.focus({ preventScroll: true });
  }

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Resets the form.
   */
  resetForm() {
    const { search } = this.refs;

    if (!search) return;

    if (search.value != '') {
      search.value = '';
      this.filterCountries();
      search.setAttribute('aria-activedescendant', '');
    }
  }

  /**
   * Focuses the search input.
   */
  focusSearchInput = () => {
    const { search } = this.refs;

    search?.focus({ preventScroll: true });
  };

  /** Re-measure virtual rows after the dropdown panel becomes visible (was `display: none`). */
  syncCountryVirtualList() {
    if (!this.#virtualSegments?.length) return;
    const s =
      this.querySelector('.popular-countries .localization-form__list-item') ||
      this.querySelector('.localization-form__list-item[role="option"][data-value]:not([data-virtual-pool])');
    if (s instanceof HTMLElement && s.offsetHeight > 0) {
      this.#sharedVirtualRowHeight = Math.max(40, s.offsetHeight);
    }
    this.#syncVirtualSegments();
  }

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
    this.#syncVirtualSegments();
  };
}

/**
 * A custom element that displays a dropdown localization form.
 *
 * @typedef {object} DropdownRefs
 * @property {HTMLButtonElement} button - The button element.
 * @property {HTMLDivElement} panel - The panel element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DropdownRefs>}
 */
class DropdownLocalizationComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Toggles the panel.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Shows the panel.
   */
  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    onAnimationEnd(this.refs.panel, () => {
      this.#updateWidth();
      this.refs.localizationForm?.resizeLanguageInput();
      this.refs.localizationForm?.syncCountryVirtualList?.();
      this.refs.localizationForm?.focusSearchInput();
    });
  }

  /**
   * Hides the panel.
   */
  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
    this.refs.localizationForm?.resetForm();
  };

  /**
   * Handles the click outside event.
   *
   * @param {PointerEvent} event - The event object.
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Updates the width of the panel.
   */
  #updateWidth() {
    const w = this.refs.localizationForm?.offsetWidth ?? 0;
    this.style.setProperty('--width', `${Math.max(w, 200)}px`);
  }

  /**
   * Handles the keyup event.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #handleKeyUp = (event) => {
    switch (event.key) {
      case 'Escape':
        this.hidePanel();
        event.stopPropagation();
        this.refs.button?.focus({ preventScroll: true });
        break;
    }
  };
}

/**
 * A custom element that displays a drawer localization form.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DrawerRefs>}
 */
class DrawerLocalizationComponent extends Component {
  /**
   * Toggles the dialog.
   *
   * @param {ToggleEvent} event - The event object.
   */
  toggle(event) {
    const { target } = event;
    const { localizationForm } = this.refs;

    if (!localizationForm || !(target instanceof HTMLDetailsElement)) return;

    const countryList = localizationForm.querySelector('.country-selector-form__wrapper');

    if (target.open) {
      if (countryList) countryList.addEventListener('scroll', this.#onCountryListScroll);
      onAnimationEnd(target, localizationForm.focusSearchInput);
    } else {
      countryList?.removeEventListener('scroll', this.#onCountryListScroll);
      localizationForm.resetForm();
    }
  }

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
  };
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}

if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}

if (!customElements.get('drawer-localization-component')) {
  customElements.define('drawer-localization-component', DrawerLocalizationComponent);
}
